/* eslint-disable complexity */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { CoreGlobalFooter } from '@care/navigation';
import { Box, Container, Grid } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { FilterProvider } from '@care/provider-react-components';
import {
  GoogleMaps,
  searchViewAutoCompleteProviderRequestOptions,
} from '@care/seeker-react-components';
import { AddressInput as AddressInputType } from '@care/seeker-react-components/dist/components/shared/SeekerLocation/types';
import { SeoGlobalNavbar } from '@/components/common/SeoGlobalNavbar';
import PageMeta from '@/components/PageMeta';
import getListPageData, {
  ActivityListingPageProps,
} from '@/components/pages/campsAndActivities/campsAndActivitiesPageListingProps';
import SearchResultsPagination from '@/components/SearchResults/Pagination';
import {
  AmplitudeEvent,
  HeaderData,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
  trackAmplitudeEvent,
} from '@/lib/analyticsHelper';
import { FilterFieldNames, ScreenName, ScreenTemplate, TealiumSlot } from '@/constants';
import { clearFilters, loadFilters } from '@/utils/filtersStorage';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import GET_ACTIVITY_SEARCH_RESULTS from '@/API/getActivitySearchResults';
import {
  getActivitySearchResults as GetActivitySearchResultsType,
  getActivitySearchResultsVariables,
} from '@/__generated__/getActivitySearchResults';
import {
  EventsSortBy,
  SEOSegmentationFilterInput,
  SEOSegmentationPageType,
} from '@/__generated__/globalTypes';
import { useResponsive } from '@/components/hooks/useResponsive';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQs from '@/components/FAQs';
import SeoLinkList from '@/components/SeoLinkList';
import {
  FiltersContext,
  SegmentationFiltersContext,
} from '@/components/contexts/SegmentationFiltersContext';
import { ArticleSection } from '@/components/common/ArticleSection/ArticleSection';
import { ExploreHeaderText } from './ExploreHeaderText';
import { ActivitiesList } from './ActivitiesList';
import { ExploreFilters } from './shared/Filters';
import ExploreNoResults from './ExploreNoResults';
import ExploreLocationSearch, { LocationState } from './shared/ExploreLocationSearch';
import { ActivitiesMap } from './ActivitiesMap/ActivitiesMap';
import { ActivitiesMapCTA } from './ActivitiesMap/ActivitiesMapCTA';
import { setFilterURL } from './helpers';
import { ProviderSignUpBanner } from './ProviderSignUpBanner';

const CampsAndActivitiesListingPage = ({
  pageData,
  deviceChar,
  initialPageNumber,
  seoSegmentId,
  ipAddress,
  botInfo,
  userGeoInfo,
  userAgent,
  local,
  showProviderSignUpBanner,
}: ActivityListingPageProps) => {
  const [segmentId, setSegmentId] = useState(seoSegmentId);
  const router = useRouter();
  const [pageNumber, setPageNumber] = useState(initialPageNumber);

  const scrollToTopRef = useRef<HTMLDivElement>(null);
  const { isSmallViewport, isLargeViewport, isMediumViewport } = useResponsive();
  const {
    seoSegmentationSearchResults: {
      pagination: initialPagination,
      activitySearchResults: initialActivitySearchResults,
      filters: initialFilters,
    },
    seoSegmentationActivityDetails: initialActivityDetails,
    getSEOSegmentationFAQs: initialFaqs,
    getSEOSegmentationLinkSections: initialLinkSections,
  } = pageData;

  const [pagination, setPagination] = useState(initialPagination);
  const [linkSections, setLinkSections] = useState(initialLinkSections);
  const [faqs, setFaqs] = useState(initialFaqs);
  const [filters, setFilters] = useState<Record<string, any> | null>({
    ...initialFilters,
    sortBy: EventsSortBy.RECOMMENDED,
  });
  const [activitySearchResults, setActivitySearchResults] = useState(initialActivitySearchResults);
  const [showMap, setShowMap] = useState<boolean>(false);

  const stringifyDeviceChar = JSON.stringify(deviceChar);

  const [activityDetails, setActivityDetails] = useState(initialActivityDetails);
  const { seoPageMetadata } = activityDetails;
  const internalLinkingHeadline = `Additional ${activityDetails.segmentNameSingular} Services`;
  const isNationalListPage = activityDetails.pageType === SEOSegmentationPageType.NATIONAL;

  const initialBreadcrumbs =
    pageData.seoSegmentationBreadcrumbs.__typename === 'SEOSegmentationBreadcrumbsSuccess'
      ? pageData.seoSegmentationBreadcrumbs.breadcrumbs
      : null;

  const [breadcrumbs, setBreadcrumbs] = useState(initialBreadcrumbs);
  const initialArticles =
    pageData.seoArticlesList.__typename === 'SEOArticlesListSuccess'
      ? pageData.seoArticlesList.seoArticlesList
      : null;

  const [articles, setArticles] = useState(initialArticles);

  const [getActivitySearchResults, { loading: isLoading }] = useLazyQuery<
    GetActivitySearchResultsType,
    getActivitySearchResultsVariables
  >(GET_ACTIVITY_SEARCH_RESULTS, {
    variables: {
      filters: null,
      pageNumber,
      seoSegmentId: segmentId,
      ipAddress,
      botInfo,
      userGeoInfo,
      userAgent,
    },
    onCompleted: (data: GetActivitySearchResultsType) => {
      const searchResultData =
        data.seoSegmentationSearchResults.__typename === 'SEOSegmentationSearchResultsSuccess'
          ? data.seoSegmentationSearchResults
          : null;

      const breadcrumbsData =
        data.seoSegmentationBreadcrumbs.__typename === 'SEOSegmentationBreadcrumbsSuccess'
          ? data.seoSegmentationBreadcrumbs.breadcrumbs
          : null;

      const activityDetailsData =
        data.seoSegmentationActivityDetails.__typename === 'SEOSegmentationActivityDetailsSuccess'
          ? data.seoSegmentationActivityDetails.segmentDetails
          : null;

      const articlesData =
        pageData.seoArticlesList.__typename === 'SEOArticlesListSuccess'
          ? pageData.seoArticlesList.seoArticlesList
          : null;

      const faqsData = data.getSEOSegmentationFAQs;
      const linkSectionsData = data.getSEOSegmentationLinkSections;

      if (searchResultData) {
        setPagination(searchResultData.pagination);
        setActivitySearchResults(searchResultData.activitySearchResults);
        if (activityDetailsData) setActivityDetails(activityDetailsData);
        if (articlesData) setArticles(articlesData);
        if (faqsData) setFaqs(faqsData);
        if (linkSectionsData) setLinkSections(linkSectionsData);
        if (breadcrumbsData) setBreadcrumbs(breadcrumbsData);
        scrollToTopRef.current?.scrollIntoView();
      }
    },
  });

  const handlePageClick = (newPage: number) => {
    const filterSelected = loadFilters() || {};
    if (filters) {
      if (filters.location) {
        filterSelected.zipcode = filters.location.zipcode;
      }
    }
    setPageNumber(newPage);
    getActivitySearchResults({
      variables: {
        filters: filterSelected,
        pageNumber: newPage,
        seoSegmentId,
        ipAddress,
        botInfo,
        userGeoInfo,
        userAgent,
      },
    });
  };
  const locationTypes = searchViewAutoCompleteProviderRequestOptions(false, true);

  const estimatedZipcode = () => {
    return getZipcodeFromGeoInfo(userGeoInfo);
  };

  const [location, setLocationState] = useState<LocationState>({
    latitude: null,
    longitude: null,
    zipcode: filters?.location?.zipcode ?? estimatedZipcode(),
  });

  const setLocation = useCallback(
    (address: AddressInputType) => {
      if (address.zip) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          location: {
            ...(prevFilters?.location || {}),
            zipcode: address.zip,
          },
        }));
      }

      setLocationState({
        zipcode: address.zip,
        longitude: address.longitude,
        latitude: address.latitude,
      });
      setPageNumber(1);
      getActivitySearchResults({
        variables: {
          filters: { zipcode: address.zip },
          pageNumber: 1,
          seoSegmentId: segmentId,
          ipAddress,
          botInfo,
          userGeoInfo,
          userAgent,
        },
      });
      if (!isNationalListPage) {
        router.push(seoSegmentId.replace(/\/[^/]+$/, '/'), undefined, { shallow: false });
      }
      scrollToTopRef.current?.scrollIntoView();
    },
    [
      botInfo,
      getActivitySearchResults,
      ipAddress,
      seoSegmentId,
      userAgent,
      userGeoInfo,
      router,
      isNationalListPage,
      segmentId,
    ]
  );

  const onFilterChange = (filterVals: Record<string, any>) => {
    const url = setFilterURL(filterVals, isNationalListPage, seoSegmentId);

    if (!url || globalThis.location.pathname === url) return;
    const { categories, subcategories, ...otherFilterVals } = filterVals;
    const filterSelected = {
      ...otherFilterVals,
      zipcode: filters?.location?.zipcode,
    };
    router.push(url, undefined, { shallow: true });
    setSegmentId(url);
    setPageNumber(1);
    getActivitySearchResults({
      variables: {
        filters: filterSelected,
        pageNumber: 1,
        seoSegmentId: url,
        ipAddress,
        botInfo,
        userGeoInfo,
        userAgent,
      },
    });
  };

  const filtersCtx = useMemo(() => {
    return {
      filters,
      setFilters: (filterVals: any, reset: boolean) => {
        setFilters(reset ? filterVals : (prevFilters) => ({ ...prevFilters, ...filterVals }));
      },
      fetchResults: (filterVals: SEOSegmentationFilterInput, resetPage: boolean) => {
        const updatedFilterVals = {
          ...filterVals,
          zipcode: filters?.location?.zipcode,
        };
        getActivitySearchResults({
          variables: {
            filters: updatedFilterVals,
            pageNumber: resetPage ? 1 : pageNumber,
            seoSegmentId: segmentId,
            ipAddress,
            botInfo,
            userGeoInfo,
            userAgent,
          },
        });
      },
    };
  }, [
    getActivitySearchResults,
    filters,
    pageNumber,
    segmentId,
    ipAddress,
    botInfo,
    userGeoInfo,
    userAgent,
  ]);

  const validateHeaderData = (data: { [key: string]: string }): HeaderData => ({
    device_os: data.device_os || 'unknown',
    is_mobile: data.is_mobile || 'false',
    mobile_browser_version: data.mobile_browser_version || '0.0',
    mobile_browser: data.mobile_browser || 'unknown',
  });

  useEffect(() => {
    const pageProperties = {
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
      seo_page_id: Number(seoPageMetadata.seoPageId),
      service_id_for_member: `${activityDetails?.serviceIdForMember}Activity`,
      slots: TealiumSlot.VISITOR,
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(validateHeaderData(deviceChar));
    }
  }, [
    deviceChar,
    stringifyDeviceChar,
    seoPageMetadata.seoPageId,
    activityDetails.serviceIdForMember,
  ]);

  useEffect(() => {
    trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, {
      seo_page_type: 'C&A SEO page',
      page_template: isNationalListPage ? 'National' : 'Local',
      seo_page_title: activityDetails?.headline ?? '',
      service_id_for_member: `${activityDetails?.serviceIdForMember}Activity`,
    });
  }, [local, isNationalListPage, activityDetails?.headline, activityDetails?.serviceIdForMember]);

  useEffect(() => {
    if (location.zipcode || (filters && Object.keys(filters).length >= 1)) {
      trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
        feature_name: 'Filters Applied',
        location: location.zipcode ?? '',
        ages: filters?.[FilterFieldNames.ages] ?? [],
        days: filters?.[FilterFieldNames.days] ?? [],
        dates: filters?.[FilterFieldNames.dates] ?? '',
        categories: filters?.[FilterFieldNames.categories] ?? '',
        activity_type: filters?.[FilterFieldNames.activityType] ?? [],
        subcategories: filters?.[FilterFieldNames.subcategories] ?? '',
      });
    }
  }, [filters, location.zipcode]);

  useEffect(() => {
    clearFilters();
  }, []);

  return (
    <SegmentationFiltersContext.Provider value={filtersCtx as FiltersContext}>
      <FilterProvider onChange={onFilterChange} data-testid="filter-provider">
        <GoogleMaps.Provider requestOptions={{ types: locationTypes }}>
          <PageMeta
            title={seoPageMetadata.title}
            canonicalUrl={seoPageMetadata.canonicalURL}
            metaDescriptions={seoPageMetadata.metaDescriptions}
            metaKeywords={seoPageMetadata.metaKeywords}
            paginationPageCount={initialPagination.numberOfPages}
            paginationCurrentPage={pageNumber}
            seoSegmentNamePlural={activityDetails.segmentNamePlural}
            isCampsAndActivitiesPage
          />
          <SeoGlobalNavbar seoNavigationLinkSection={pageData.seoNavigationLinkSection} />
          {showProviderSignUpBanner && <ProviderSignUpBanner />}
          <ExploreFilters
            activityCategory={activityDetails.category}
            activitySubcategory={activityDetails.subcategory}
            LocationInput={
              <ExploreLocationSearch
                address={location}
                onChange={setLocation}
                isLoading={isLoading}
              />
            }
            isMobile={isSmallViewport}
          />
          {isSmallViewport && <ActivitiesMapCTA showMap={showMap} setShowMap={setShowMap} />}
          {isSmallViewport && showMap ? (
            <Grid container component="main" height="100%" width="100%" item xs={12}>
              <ActivitiesMap
                activitiesSearchResults={activitySearchResults}
                userGeoInfo={userGeoInfo}
                location={location}
              />
            </Grid>
          ) : (
            <>
              <Container maxWidth="xl" ref={scrollToTopRef} sx={{ px: 3 }}>
                <Grid container component="main" item xs={12}>
                  <Grid item xs={12} sm={9} md={9} mt={4} pr={3}>
                    {!isSmallViewport && breadcrumbs && (
                      <Grid item xs={12}>
                        <Breadcrumbs breadcrumbs={breadcrumbs} />
                      </Grid>
                    )}
                    {activityDetails.headline && (
                      <ExploreHeaderText
                        headline={activityDetails.headline}
                        hasResults={isLoading || !!activitySearchResults.length}
                      />
                    )}
                    {activitySearchResults.length ? (
                      <ActivitiesList
                        activityDetails={activityDetails}
                        isLoading={isLoading}
                        searchResult={activitySearchResults}
                      />
                    ) : (
                      <ExploreNoResults
                        noResults
                        isNationalListPage={isNationalListPage}
                        isMobileOrSmaller={isSmallViewport}
                      />
                    )}

                    <SearchResultsPagination
                      onPageClick={handlePageClick}
                      pagination={pagination}
                      segmentId={seoSegmentId}
                      pageSelected={pageNumber}
                    />
                  </Grid>
                  {(isMediumViewport || isLargeViewport) && (
                    <Grid item sm={3} md={3}>
                      <ActivitiesMap
                        activitiesSearchResults={activitySearchResults}
                        userGeoInfo={userGeoInfo}
                        location={location}
                      />
                    </Grid>
                  )}
                </Grid>
              </Container>

              {articles?.articles && <ArticleSection articles={articles.articles} />}

              {faqs && (
                <Container maxWidth="xl">
                  <Box
                    sx={{
                      py: { xs: 2, xl: 0 },
                      mx: { xl: 10 },
                    }}>
                    <FAQs faqs={faqs as any} />
                  </Box>
                </Container>
              )}

              {linkSections && (
                <Box
                  sx={{
                    backgroundColor: 'background.secondary',
                  }}>
                  <Container maxWidth="xl">
                    <Box
                      sx={{
                        mt: 3,
                        '& .MuiContainer-root': {
                          margin: '0 !important',
                        },
                        py: { xs: 6, sm: 10 },
                        mx: { sm: 10 },
                      }}>
                      <SeoLinkList
                        seoLinkList={linkSections}
                        internalLinkingHeadline={internalLinkingHeadline}
                        seoSegmentNamePlural={activityDetails.segmentNamePlural}
                      />
                    </Box>
                  </Container>
                </Box>
              )}

              {isSmallViewport && breadcrumbs && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    pb: 6,
                    pl: 2,
                    pt: 2,
                  }}>
                  <Breadcrumbs breadcrumbs={breadcrumbs} />
                </Grid>
              )}
              <CoreGlobalFooter variant="inverse" />
            </>
          )}
        </GoogleMaps.Provider>
      </FilterProvider>
    </SegmentationFiltersContext.Provider>
  );
};

export default CampsAndActivitiesListingPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return getListPageData(context);
};
